import {
  type DataSettingsManager,
  type DataSettings,
  type DataSettingsClient,
  type SettingsChangeListener,
} from '@wix/wix-data-client-common-standalone'
import type { DuplexerClient } from '../duplexer/createDuplexerClient'

interface Params {
  dataSettingsClient: DataSettingsClient
  duplexerClient: DuplexerClient
}

const defaultDataSettings: Required<DataSettings> = {
  contentManagerEnabled: false,
  sandboxEnabled: false,
  sandboxPubliclyAccessible: false,
  appCollectionsVisible: null,
  sandboxDataRemovedAt: null,
  dataCachingEnabled: true,
  revision: null,
}

export const createDataSettingsManager = ({
  dataSettingsClient,
  duplexerClient,
}: Params): DataSettingsManager => {
  let dataSettings: Required<DataSettings> | null = null

  const settingsChangeListeners: SettingsChangeListener[] = []

  const update = (settingsToUpdate: DataSettings) => {
    const oldSettings = dataSettings

    if (hasSettingsChanged(oldSettings, settingsToUpdate)) {
      dataSettings = {
        ...defaultDataSettings,
        ...oldSettings,
        ...settingsToUpdate,
      }
      notifyListeners(settingsChangeListeners, oldSettings, dataSettings)
    }
  }

  const getDataSettings = () =>
    dataSettings ? { ...dataSettings } : defaultDataSettings

  return {
    async initialize() {
      duplexerClient.onDataSettingsChange(
        ({ changeType, ...settingsToUpdate }) => update(settingsToUpdate),
      )
      try {
        update(await dataSettingsClient.get())
      } catch (error) {
        update({})
        throw new Error('Failed to initialize data settings', { cause: error })
      }
    },
    getDataSettings,
    async updateDataSettings(settingsToUpdate) {
      update(await dataSettingsClient.update(settingsToUpdate))
      return getDataSettings()
    },
    onSettingsChange(listener) {
      settingsChangeListeners.push(listener)
    },
  }
}

function notifyListeners(
  listeners: SettingsChangeListener[],
  oldSettings: Required<DataSettings> | null,
  newSettings: Required<DataSettings>,
) {
  for (const listener of listeners) {
    listener(oldSettings, newSettings)
  }
}

function hasSettingsChanged(
  oldSettings: Required<DataSettings> | null,
  settingsToUpdate: DataSettings,
) {
  if (!oldSettings?.revision) {
    return true
  }
  return (
    !!settingsToUpdate.revision &&
    Number.parseInt(settingsToUpdate.revision) >
      Number.parseInt(oldSettings.revision)
  )
}
